import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Group, Burger, AppShell, Image, em, Avatar, Box, Anchor, SimpleGrid, Center, Menu } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { EmptyMessage, Loader, Text } from 'components';
import { AppConstants, AuthorityConstants, RouteConstants } from 'constant';
import { PublicService } from 'services';
import { UserResponse } from 'types';
import { StorageUtils, UserUtils } from 'utils';

import classes from 'styles/display.module.css';
import logo from 'assets/logo-white.svg';
import ChangePasswordModal from 'components/modal/change-password';
import { IconChevronDown } from '@tabler/icons-react';

const { STORAGE_USER } = AppConstants;
const { ROLE_CC, ROLE_CR, ROLE_USR_REC, ROLE_USR, ROLE_CHT_R, ROLE_CHT_R_MY, ROLE_CONTROL, ROLE_PRF_CUD, ROLE_TODO_R, ROLE_TODO_CUD } = AuthorityConstants;
const { ROUTE_LOGIN, ROUTE_CLIENTS, ROUTE_MY_CLIENTS, ROUTE_CARDS, ROUTE_USER_RECOMMENDATIONS, ROUTE_USERS, ROUTE_CHAT, ROUTE_CONTROL, ROUTE_TODO, ROUTE_PROFILE, ROUTE_PERMISSIONS } = RouteConstants;

interface CustomLayout {
  authority?: string;
  children?: any;
  loading?: boolean;
  title?: string;
  padding?: any;
}

interface RouterProps {
  href?: string;
  title?: string;
  icon?: React.ReactElement;
  children?: RouterProps[];
  active?: boolean;
}

interface NavbarViewProps {
  opened: boolean;
  toggle: () => void;
  logout: () => void;
  routes: any[];
  t: TFunction;
  user?: UserResponse;
  navigate: (href: string) => void;
  showChangePassword: () => void;
}

interface  UserAvatarProps {
  user?: UserResponse;
}

const LayoutStandard = ({ authority, children, loading, title, padding }: CustomLayout) => {
  const [user] = useState<UserResponse | undefined>(StorageUtils.getObject(STORAGE_USER))
  const [opened, { toggle }] = useDisclosure(false);
  const [routes, setRoutes] = useState<any[] | undefined>(undefined);
  const [changePasswordVisible, setChangePasswordVisible] = useState<boolean>(false);
  const isMobile = useMediaQuery(`(max-width: ${em(768)})`);
  const navigate = useNavigate();
  const { t } = useTranslation();


  useEffect(() => {
    document.title = title ? title.toString() : 'Webkredit.ch';
  }, [title]);

  useEffect(() => {
    if (!user) {
      navigate(RouteConstants.ROUTE_LOGIN);
      return;
    }
    const tempRouter: RouterProps[] = [];
    if (UserUtils.hasAuthority(ROLE_CR)) {
      tempRouter.push({ title: 'header.clients.title', active: window.location.href.includes(ROUTE_CLIENTS), children: [
        { href: ROUTE_CLIENTS, title: 'header.clients.all', active: window.location.href.includes(ROUTE_CLIENTS) },
        { href: ROUTE_CLIENTS + '?source=mobile', title: 'header.clients.mobile', active: window.location.href.includes(ROUTE_CLIENTS) },
        { href: ROUTE_CLIENTS + '?source=web', title: 'header.clients.web', active: window.location.href.includes(ROUTE_CLIENTS) },
        { href: ROUTE_CLIENTS + '?source=other', title: 'header.clients.other', active: window.location.href.includes(ROUTE_CLIENTS) }
      ]})
      tempRouter.push({ title: 'header.my_clients.title', active: window.location.href.includes(ROUTE_MY_CLIENTS), children: [
        { href: ROUTE_MY_CLIENTS, title: 'header.my_clients.all', active: window.location.href.includes(ROUTE_MY_CLIENTS) },
        { href: ROUTE_MY_CLIENTS + '?source=mobile', title: 'header.my_clients.mobile', active: window.location.href.includes(ROUTE_MY_CLIENTS) },
        { href: ROUTE_MY_CLIENTS + '?source=web', title: 'header.my_clients.web', active: window.location.href.includes(ROUTE_MY_CLIENTS) },
        { href: ROUTE_MY_CLIENTS + '?source=other', title: 'header.my_clients.other', active: window.location.href.includes(ROUTE_MY_CLIENTS) }
      ]})
    }
    if (UserUtils.hasAnyAuthority([ROLE_CHT_R, ROLE_CHT_R_MY])) {
      tempRouter.push({ href: ROUTE_CHAT, title: 'header.chat', active: window.location.href.includes(ROUTE_CHAT) })
    }
    if (UserUtils.hasAuthority(ROLE_USR_REC)) {
      tempRouter.push({ href: ROUTE_USER_RECOMMENDATIONS, title: 'header.recommendations', active: window.location.href.includes(ROUTE_USER_RECOMMENDATIONS) })
    }
    if (UserUtils.hasAuthority(ROLE_CC)) {
      tempRouter.push({ href: ROUTE_CARDS, title: 'header.cards', active: window.location.href.includes(ROUTE_CARDS) })
    }
    if (UserUtils.hasAuthority(ROLE_USR)) {
      tempRouter.push({ href: ROUTE_USERS, title: 'header.users', active: window.location.href.includes(ROUTE_USERS) })
    }
    if (UserUtils.hasAuthority(ROLE_CONTROL) && process.env.REACT_APP_NODE_ENV !== 'production') {
      tempRouter.push({ href: ROUTE_CONTROL, title: 'header.control', active: window.location.href.includes(ROUTE_CONTROL) })
    }
    if (UserUtils.hasAnyAuthority([ROLE_TODO_R, ROLE_TODO_CUD])) {
      tempRouter.push({ href: ROUTE_TODO, title: 'header.todo', active: window.location.href.includes(ROUTE_TODO) })
    }
    if (UserUtils.hasAuthority(ROLE_PRF_CUD)) {
      tempRouter.push({ href: ROUTE_PERMISSIONS, title: 'header.permissions', active: window.location.href.includes(ROUTE_PERMISSIONS) })
    }
    setRoutes(tempRouter);
  }, [navigate, user]);

  const logout = useCallback(async () => {
    await PublicService.logout();
    navigate(ROUTE_LOGIN);
  }, [navigate]);

  if (!routes || !user) {
    return <></>
  }

  return (
    <AppShell
      layout={'alt'}
      header={{ height: 56 }}
      navbar={{ width: 300, breakpoint: 'sm', collapsed: { desktop: true, mobile: !opened } }}
      padding={padding ?? 'md'}
    >
      <AppShell.Header bg={'#2e2b3c'}>
        <Group h="100%" px="md">
          <ChangePasswordModal opened={changePasswordVisible} onClose={setChangePasswordVisible} t={t} />
          {isMobile
          ? <RenderMobileView
              opened={opened}
              toggle={toggle}
              logout={logout}
              routes={routes}
              user={user}
              navigate={navigate}
              showChangePassword={() => setChangePasswordVisible(true)}
              t={t} />
          : <RenderWebView
              opened={opened}
              toggle={toggle}
              logout={logout}
              routes={routes}
              navigate={navigate}
              user={user}
              showChangePassword={() => setChangePasswordVisible(true)}
              t={t} />
          }
        </Group>
      </AppShell.Header>
      <AppShell.Main style={{ display: 'flex', flexDirection: 'column' }}>
        {!authority || UserUtils.hasAuthority(authority) ?
          loading ?
            <Center h={'100vh'}><Loader /></Center> : <Box style={{ flexGrow: 1 }}>{children}</Box>
          : <EmptyMessage message={t('authority.no')} />
        }
      </AppShell.Main>
    </AppShell>
  )
};

const RenderMobileView = ({ opened, toggle, logout, routes, t, user, navigate, showChangePassword }: NavbarViewProps) => (
  <Group flex={1} justify={'space-between'} align={'center'}>
    <Burger opened={opened} onClick={toggle} size={'sm'} hiddenFrom={'sm'} color={'var(--mantine-color-white)'} />
    <AppShell.Navbar bg={'#2e2b3c'}>
      <Burger mx="md" my='md' opened={opened} onClick={toggle} size={'sm'} hiddenFrom={'sm'} color={'var(--mantine-color-white)'} />
      <Group flex={1} justify={'space-between'} align={'center'}>
        <SimpleGrid cols={1} verticalSpacing={'xs'}>
          {routes.map((item, key) => (
            item.href ?
              <Anchor key={`drawer-${key}`} onClick={() => navigate(item.href)} underline={'never'} className={classes.navbarItem}>
                {item.icon}
                <Text c={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} size={'sm'}>{t(item.title)}</Text>
              </Anchor>
            : <Menu
                shadow={'md'}
                withArrow={true}
                width={200}
                position={'bottom'}
                arrowOffset={10}
                offset={16}
                zIndex={99999}
              >
                <Menu.Target>
                  <Anchor key={`drawer-${key}`} underline={'never'} className={classes.navbarItem}>
                    {item.icon}
                    <Text c={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} size={'sm'}>{t(item.title)}</Text>
                    <IconChevronDown size={18} stroke={1.5} color={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} />
                  </Anchor>
                </Menu.Target>
                <Menu.Dropdown>
                  {item.children.map((child: RouterProps, childIndex: number) => (
                    <Menu.Item
                      key={`item-${childIndex}`}
                      leftSection={child.icon}
                      fs={'lg'}
                      onClick={() => navigate(child.href!)}
                    >
                      {child.title && t(child.title)}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
          ))}
        </SimpleGrid>
      </Group>
    </AppShell.Navbar>
    <Image src={logo} w={40} h={34} style={{ objectFit: 'contain' }} />
    <Group>
      <Menu trigger={'click'} withArrow={true}>
        <Menu.Target>
          <Box ta={'center'} style={{ cursor: 'pointer' }}>
            <UserAvatar user={user} />
          </Box>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => navigate(ROUTE_PROFILE)}>
            {t('profile')}
          </Menu.Item>
          <Menu.Item onClick={showChangePassword}>
            {t('password.change.title')}
          </Menu.Item>
          <Menu.Item onClick={logout} color={'var(--mantine-color-error)'}>
            {t('logout')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  </Group>
)

const RenderWebView = ({ navigate, logout, routes, user, t, showChangePassword }: NavbarViewProps) => (
  <Group flex={1} justify={'space-between'} align={'center'}>
    <Group>
      <Image src={logo} w={40} h={34} style={{ objectFit: 'contain' }} />
      <Group>
        {routes.map((item, key) => (
          item.href ?
            <Anchor key={`drawer-${key}`} onClick={() => navigate(item.href)} underline={'never'} className={classes.navbarItem}>
              {item.icon}
              <Text c={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} size={'sm'}>{t(item.title)}</Text>
            </Anchor>
          :
          <Menu
            key={`drawer-${key}`}
            shadow={'md'}
            withArrow={true}
            width={200}
            position={'bottom'}
            arrowOffset={10}
            offset={16}
            zIndex={99999}
          >
            <Menu.Target>
              <Anchor underline={'never'} className={classes.navbarItem}>
                {item.icon}
                <Text c={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} size={'md'}>{t(item.title)}</Text>
                <IconChevronDown size={18} stroke={1.5} color={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} />
              </Anchor>
            </Menu.Target>
            <Menu.Dropdown>
              {item.children.map((child: RouterProps, childIndex: number) => (
                <Menu.Item
                  key={`item-${childIndex}`}
                  leftSection={child.icon}
                  fs={'lg'}
                  onClick={() => navigate(child.href!)}
                >
                  {child.title && t(child.title)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ))}
      </Group>
    </Group>
    <Group>
      <Text c={'var(--mantine-color-white)'} size={'md'}>{user?.fullName}</Text>
      <Menu trigger={'click'} withArrow={true}>
        <Menu.Target>
          <Box mb={6} ta={'center'} style={{ cursor: 'pointer' }}>
            <UserAvatar user={user} />
          </Box>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => navigate(ROUTE_PROFILE)}>
            {t('profile')}
          </Menu.Item>
          <Menu.Item onClick={showChangePassword}>
            {t('password.change.title')}
          </Menu.Item>
          <Menu.Item onClick={logout} color={'var(--mantine-color-error)'}>
            {t('logout')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  </Group>
)

const UserAvatar = ({ user }: UserAvatarProps) => {
  let imageUrl = null;
  if (user?.profileImage) {
    imageUrl = `${process.env.REACT_APP_API_URL_ATTACHMENTS}/${user.profileImage}`;
  }
  return <Avatar size={32} src={imageUrl} radius={'xl'} className={classes.initials} />
}
export default LayoutStandard;